import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useMusicPlayer } from "@/context/MusicPlayerContext";
import { useI18n } from "@/context/I18n";
import TrackRow from "@/components/music/TrackRow";
import { toast } from "sonner";
import { ListMusic, Loader2, Music2, Play, Search, User } from "lucide-react";

export default function MusicLibrary() {
  const [tracks, setTracks] = useState(null);
  const [artists, setArtists] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [q, setQ] = useState("");
  const { playQueue } = useMusicPlayer();
  const { t } = useI18n();

  const loadTracks = (search) =>
    api.get("/music/library").then((r) => {
      const term = (search || "").trim().toLowerCase();
      const all = r.data.tracks || [];
      setTracks(
        term
          ? all.filter(
              (x) =>
                (x.title || "").toLowerCase().includes(term) ||
                (x.artist || "").toLowerCase().includes(term)
            )
          : all
      );
    });

  useEffect(() => {
    loadTracks("");
    api.get("/music/artists").then((r) => setArtists(r.data.artists)).catch(() => {});
    api.get("/music/playlists").then((r) => setPlaylists(r.data.playlists)).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => loadTracks(q), 300);
    return () => clearTimeout(timer);
  }, [q]);

  const removeTrack = async (track) => {
    if (!window.confirm(`${track.title}?`)) return;
    await api.delete(`/music/tracks/${track.id}`);
    toast.success(t("track_removed"));
    loadTracks(q);
  };

  return (
    <div className="p-4 md:p-8 pb-6">
      <div className="flex items-end justify-between mb-6">
        <h1 className="font-heading text-3xl font-bold">{t("your_library")}</h1>
        {tracks && tracks.length > 0 && (
          <button data-testid="library-play-all" onClick={() => playQueue(tracks, 0)} className="bg-[#FF2A4D] hover:bg-[#E61C3D] rounded-full px-5 py-2.5 font-semibold flex items-center gap-2 transition-colors">
            <Play className="w-4 h-4" fill="white" /> {t("play_all")}
          </button>
        )}
      </div>

      {artists.length > 0 && (
        <div className="flex gap-3 mb-6 flex-wrap">
          {artists.slice(0, 12).map((a) => (
            <Link key={a.name} to={`/app/artist/${encodeURIComponent(a.name)}`} className="flex items-center gap-2 bg-white/5 hover:bg-white/10 rounded-full pl-1 pr-4 py-1 transition-colors">
              {a.thumbnail ? <img src={a.thumbnail} className="w-8 h-8 rounded-full object-cover" alt="" /> : <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"><User className="w-4 h-4 text-zinc-500" /></div>}
              <span className="text-sm">{a.name}</span>
            </Link>
          ))}
        </div>
      )}

      {playlists.length > 0 && (
        <div className="flex gap-4 mb-8 overflow-x-auto pb-2">
          {playlists.map((pl) => (
            <Link key={pl.id} to={`/app/playlist/${pl.id}`} className="shrink-0 w-40 bg-white/5 hover:bg-white/10 rounded-xl p-3 transition-colors">
              <div className="w-full aspect-square rounded-lg bg-gradient-to-br from-[#FF2A4D]/70 to-purple-800 flex items-center justify-center">
                <ListMusic className="w-8 h-8 text-white/80" />
              </div>
              <p className="text-sm font-semibold truncate mt-2">{pl.name}</p>
              <p className="text-xs text-zinc-400 truncate">{pl.track_count} {t("tracks")}</p>
            </Link>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 bg-white/5 rounded-lg px-3 max-w-sm mb-3">
        <Search className="w-4 h-4 text-zinc-500" />
        <input data-testid="library-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_library_ph")} className="flex-1 bg-transparent py-2 text-sm focus:outline-none" />
      </div>

      {tracks === null ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
      ) : tracks.length === 0 ? (
        <div className="text-center py-16 text-zinc-500">
          <Music2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>{t("nothing_here")} <Link to="/app/search" className="text-[#FF2A4D]">{t("ask_admin")}</Link>.</p>
        </div>
      ) : (
        <div>
          {tracks.map((tr, i) => (
            <TrackRow key={tr.id} track={tr} index={i} tracks={tracks} onRemove={removeTrack} />
          ))}
        </div>
      )}
    </div>
  );
}
